/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable import/no-unused-modules */
import { useEffect, useState } from 'react'

import { GridColDef } from '../../../DataGrid'
import {
  AlertState,
  convertColumns,
  ListFieldColumnDefinition,
  ListFieldData,
  ListFieldRowDefinition,
  OnChangeCustomEvent,
} from '../definitions'
export interface ListFieldProps {
  apiData?: ListFieldData
  onChange?: () => ListFieldRowDefinition[]
}

/**
 * Represents the return type of the `useListField` hook.
 */
type UseListFieldHookReturnType = {
  editModalOpen: boolean
  currentEditId: string | null
  deleteModalOpen: boolean
  currentDeleteId: string | null
  drawerOpen: boolean
  alertState: AlertState
  columns: GridColDef[]
  rows: ListFieldRowDefinition[]
  fields: ListFieldColumnDefinition[]
  title: string
  getRow: (id: string) => ListFieldRowDefinition | undefined
  handleOpenEditElementModal: (id?: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleCloseEditElementModal: (save?: boolean, editFormState?: any) => void
  handleOpenDeleteElementModal: (id: string) => void
  handleCloseDeleteElementModal: (deleteSuccess?: boolean) => void
  closeAlert: () => void
  openHistoryDrawer: () => void
  handleCloseDrawer: () => void
}

/**
 * Custom hook for managing a list field.
 *
 * @param apiData - The initial data for the list field.
 * @param onChange - Optional callback function that can be used to subscribe to changes in the rows.
 * @returns An object containing the state and functions for managing the list field.
 */
/**
 * Custom hook for managing a list field.
 * @param apiData - The initial data for the list field.
 * @param onChange - Optional callback function to be called when the list field data changes.
 * @returns An object containing the state and functions for managing the list field.
 */
export const useListField = (
  apiData?: ListFieldData,
  onChange?: (evt: OnChangeCustomEvent) => void,
  customEditCallback?: () => void,
  customDeleteCallback?: () => void,
): UseListFieldHookReturnType => {
  /**
   * The title of the list field.
   */
  const [title, setTitle] = useState<string>('')

  /**
   * Flag indicating whether the edit modal is open.
   */
  const [editModalOpen, setNewOrEditModalOpen] = useState<boolean>(false)

  /**
   * The ID of the currently edited element.
   */
  const [currentEditId, setCurrentEditId] = useState<string | null>(null)

  /**
   * Flag indicating whether the delete modal is open.
   */
  const [deleteModalOpen, setDeleteOpen] = useState<boolean>(false)

  /**
   * The ID of the currently deleted element.
   */
  const [currentDeleteId, setCurrentDeleteId] = useState<string | null>(null)

  /**
   * Flag indicating whether the drawer is open.
   */
  const [drawerOpen, setDrawerOpen] = useState(false)

  /**
   * The state of the alert.
   */
  const [alertState, setAlertState] = useState<AlertState>({ message: '', visible: false })

  /**
   * The columns of the list field.
   */
  const [columns, setColumns] = useState<GridColDef[]>([])

  /**
   * The fields of the list field.
   */
  const [fields, setFields] = useState<ListFieldColumnDefinition[]>([])

  /**
   * The rows of the list field.
   */
  const [rows, setRows] = useState<ListFieldRowDefinition[]>([])

  /**
   * Initialize the list field with the provided API data.
   * @param apiData - The initial data for the list field.
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const sortColumns = (columns: GridColDef[]): GridColDef[] => {
    return [...columns].sort((a, b) => {
      if (a.field === 'actions') return 1
      if (b.field === 'actions') return -1
      return 0
    })
  }

  useEffect(() => {
    setTitle(apiData?.title || 'Data')
    setFields(apiData?.columns || [])
    const sortedMappedColumns = apiData && apiData.columns ? sortColumns(convertColumns(apiData.columns)) : []

    setColumns(sortedMappedColumns)

    const mappedRows = apiData?.rows.map((row: ListFieldRowDefinition) => ({
      ...row,
      deleteCallBack: (id: string) => {
        handleOpenDeleteElementModal(id)
      },
      editCallBack: (id: string) => {
        handleOpenEditElementModal(id)
      },
    })) as ListFieldRowDefinition[]
    setRows(mappedRows)
  }, [apiData])

  // useEffect(() => {
  //   if (onChange) {
  //     onChange(rows)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [rows])

  /**
   * Get a row by its ID.
   * @param id - The ID of the row.
   * @returns The row with the specified ID, or null if not found.
   */
  const getRow = (id: string) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const row = rows.find((row) => row.id === id)
    return row
  }

  /**
   * Open the edit element modal.
   * @param id - Optional ID of the element to edit. If not provided, a new element will be created.
   */
  const handleOpenEditElementModal = (id?: string) => {
    if (id) {
      setCurrentEditId(id)
    } else {
      setCurrentEditId(null)
    }
    setNewOrEditModalOpen(true)
  }

  /**
   * Close the edit element modal.
   * @param save - Flag indicating whether to save the changes made in the edit element modal.
   * @param editFormState - The state of the edit form.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCloseEditElementModal = (save?: boolean, editFormState?: any) => {
    if (save) {
      if (currentEditId) {
        setRows(
          rows.map((row) => {
            if (row.id === currentEditId) {
              return {
                ...editFormState,
                id: currentEditId,
                deleteCallBack: () => {
                  handleOpenDeleteElementModal(currentEditId)
                },
                editCallBack: () => {
                  handleOpenEditElementModal(currentEditId)
                },
              }
            }
            return row
          }),
        )
        if (onChange) {
          onChange({ type: 'UPDATE', value: { id: currentEditId, editFormState } })
        }
        if (customEditCallback) {
          customEditCallback()
        }
      } else {
        const newId = crypto.randomUUID()
        if (onChange) {
          onChange({ type: 'CREATE', value: { id: newId, editFormState } })
        }
        if (customEditCallback) {
          customEditCallback()
        }
        setRows([
          ...rows,
          {
            ...editFormState,
            state: 'planned',
            id: newId,
            deleteCallBack: () => {
              handleOpenDeleteElementModal(newId)
            },
            editCallBack: () => {
              handleOpenEditElementModal(newId)
            },
          },
        ])
      }
    }
    setNewOrEditModalOpen(false)
    setCurrentEditId(null)
    if (save) {
      setAlertState({ message: 'Therapy was saved successfully', visible: true })
    }
  }

  useEffect(() => {
    if (alertState.visible) {
      setTimeout(() => closeAlert(), 6000)
    }
  }, [alertState.visible])

  /**
   * Open the delete element modal.
   * @param id - The ID of the element to delete.
   */
  const handleOpenDeleteElementModal = (id: string) => {
    setDeleteOpen(true)
    setCurrentDeleteId(id)
  }

  /**
   * Close the delete element modal.
   * @param deleteSuccess - Flag indicating whether the delete operation was successful.
   */
  const handleCloseDeleteElementModal = (deleteSuccess?: boolean) => {
    if (deleteSuccess) {
      setRows(rows.filter((row) => row.id !== currentDeleteId))
      setAlertState({ message: 'Therapy was deleted successfully', visible: true })
      if (onChange) {
        onChange({ type: 'DELETE', value: { id: currentDeleteId ?? '' } })
      }
      if (customDeleteCallback) {
        customDeleteCallback()
      }
    }
    setDeleteOpen(false)
    setCurrentDeleteId(null)
  }

  /**
   * Close the alert.
   */
  const closeAlert = () => {
    setAlertState({ message: '', visible: false })
  }
  /**
   * Open the history drawer.
   */
  const openHistoryDrawer = () => {
    setDrawerOpen(true)
  }

  /**
   * Close the history drawer.
   */
  const handleCloseDrawer = () => {
    setDrawerOpen(false)
  }

  return {
    editModalOpen,
    currentEditId,
    deleteModalOpen,
    currentDeleteId,
    drawerOpen,
    alertState,
    columns,
    rows,
    fields,
    title,
    getRow,
    handleOpenEditElementModal,
    handleCloseEditElementModal,
    handleOpenDeleteElementModal,
    handleCloseDeleteElementModal,
    closeAlert,
    openHistoryDrawer,
    handleCloseDrawer,
  }
}
